import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Palette } from 'lucide-react';

import { Button } from '@/components/ui/button';
import ConfirmDialog from '@/components/ui/ConfirmDialog';
import { apiFetch } from '@/lib/api';

/**
 * Saves this production's look (palette, lighting, lens, grade) to the Style Catalog
 * so a future production can be shot to match it.
 */
export default function PromoteStyleButton({ productionId }: { productionId: string }) {
  const { t } = useTranslation('production');
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const promote = async (name: string) => {
    setBusy(true);
    try {
      const resp = await apiFetch('/api/styles/promote', {
        method: 'POST',
        body: JSON.stringify({ production_id: productionId, name }),
      });
      setMsg(resp.ok ? t('final.promoteSaved') : t('final.promoteFailed'));
    } catch {
      setMsg(t('final.promoteFailed'));
    } finally {
      setBusy(false);
      setTimeout(() => setMsg(null), 4000);
    }
  };

  return (
    <>
      <Button variant="outline" disabled={busy} onClick={() => setAsking(true)}>
        <Palette className="mr-2 h-4 w-4" />
        {busy ? t('state.saving', { ns: 'common' }) : t('final.promoteStyle')}
      </Button>
      {msg && <span className="self-center text-xs text-muted-foreground">{msg}</span>}

      <ConfirmDialog
        open={asking}
        onOpenChange={setAsking}
        title={t('final.promoteTitle')}
        description={t('final.promoteDescription')}
        warning={t('final.promoteWarning')}
        promptLabel={t('final.promoteNameLabel')}
        promptPlaceholder={t('final.promoteNamePlaceholder')}
        confirmLabel={t('final.promoteConfirm')}
        onConfirm={promote}
      />
    </>
  );
}
